import React, { useState, useEffect } from 'react';
import { TestLogEntry, SeasonalPlan, WaterBodyType } from '../types';
import { getSeasonalSuggestion, generateSeasonalPlan, generateMaintenanceCalendar } from '../services/geminiService';
import { useLocalStorage } from '../hooks/useLocalStorage';

interface MaintenanceTabProps {
  log: TestLogEntry[];
  poolVolume: number;
  waterBody?: WaterBodyType;
}

// ─── Routine task definitions ────────────────────────────────────────────────

const POOL_ROUTINE_TASKS: Record<string, { title: string; description: string }[]> = {
  'Daily (or as needed)': [
    { title: 'Skim Surface', description: 'Remove leaves, bugs, and debris from the pool surface.' },
    { title: 'Check Baskets', description: 'Check and empty skimmer and pump baskets if full.' },
  ],
  Weekly: [
    { title: 'Test Water', description: 'Perform a full water test (FC, CC, pH, TA, CH, CYA) at least once a week.' },
    { title: 'Brush Walls & Floor', description: 'Brush the entire pool surface to remove dirt and prevent algae growth.' },
    { title: 'Vacuum Pool', description: 'Vacuum the pool to remove debris that has settled on the floor.' },
    { title: 'Check Filter Pressure', description: 'Backwash or clean the filter if 8–10 PSI above clean starting pressure.' },
    { title: 'Shock Pool', description: 'Add a dose of shock, especially after heavy use or rain.' },
    { title: 'Check Water Level', description: 'Ensure water level is halfway up the skimmer opening.' },
  ],
  Monthly: [
    { title: 'Deep-Clean Filter', description: 'Chemical clean cartridge or backwash sand/DE filter thoroughly.' },
    { title: 'Inspect Equipment', description: 'Check pump, filter, and equipment for leaks or wear.' },
    { title: 'Test LSI', description: 'Use the Water Balance tab to check LSI and prevent scaling/corrosion.' },
  ],
};

const HOT_TUB_ROUTINE_TASKS: Record<string, { title: string; description: string }[]> = {
  'Before / After Each Use': [
    { title: 'Rinse Off Before Entry', description: 'Shower before entering to reduce body-oil and lotion contamination.' },
    { title: 'Test Sanitizer', description: 'Quick test strip check — ensure FC 3–5 ppm or Bromine 3–5 ppm before soaking.' },
    { title: 'Replace Cover', description: 'Always cover the spa when not in use to retain heat and prevent debris.' },
  ],
  '2–3× Per Week': [
    { title: 'Full Water Test', description: 'Test FC/Br, CC, pH, and TA. Adjust as needed after each test.' },
    { title: 'Shock Spa', description: 'Use non-chlorine shock (MPS) or chlorine shock after heavy use to oxidize contaminants.' },
    { title: 'Check Water Level', description: 'Top off if water level has dropped below the skimmer intake.' },
  ],
  Weekly: [
    { title: 'Clean Filter Cartridge', description: 'Rinse the filter with a garden hose. Hot tubs need more frequent filter cleaning than pools.' },
    { title: 'Wipe Waterline', description: 'Clean the spa shell at the waterline with a spa surface cleaner to remove oil and scale deposits.' },
    { title: 'Check Cover', description: 'Inspect the cover for tears, waterlogging, or odor that indicate mold/mildew.' },
  ],
  Monthly: [
    { title: 'Deep-Clean Filter', description: 'Soak filter cartridge overnight in a chemical filter cleaner solution, then rinse thoroughly.' },
    { title: 'Inspect Jets & Plumbing', description: 'Turn on jets and check for leaks, poor flow, or unusual noise.' },
    { title: 'Test Total Dissolved Solids (TDS)', description: 'If TDS is 1500 ppm above fill-water baseline, plan a drain & refill.' },
  ],
  'Every 3–4 Months': [
    { title: 'Drain & Refill', description: 'Completely drain the spa, clean the shell, and refill with fresh water. Hot tubs accumulate dissolved solids much faster than pools due to small volume.' },
    { title: 'Clean Plumbing Lines', description: 'Before draining, add a spa line flush product and run the jets for 20–30 minutes to purge biofilm from the pipes.' },
    { title: 'Inspect Heater & Plumbing', description: 'While drained, inspect the heater element, O-rings, and any visible plumbing for scale or wear.' },
  ],
};

// ─── Seasonal Care sub-component ─────────────────────────────────────────────

interface SeasonalCareProps {
  poolVolume: number;
  location: string;
  setLocation: (loc: string) => void;
  waterBody: WaterBodyType;
}

const SeasonalCare: React.FC<SeasonalCareProps> = ({ poolVolume, location, setLocation, waterBody }) => {
  const [suggestion, setSuggestion] = useState('');
  const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false);
  const [plan, setPlan] = useState<SeasonalPlan | null>(null);
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  const [error, setError] = useState('');

  const handleGetSuggestion = async () => {
    if (!location) { setError('Please enter a location.'); return; }
    setIsLoadingSuggestion(true); setError(''); setSuggestion('');
    try {
      const result = await getSeasonalSuggestion(location);
      setSuggestion(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An error occurred.');
    } finally {
      setIsLoadingSuggestion(false);
    }
  };

  const handleGeneratePlan = async (task: 'Winterizing' | 'Summer Start-up') => {
    if (!location) { setError('Please enter a location to generate a tailored plan.'); return; }
    setIsLoadingPlan(true); setError(''); setPlan(null);
    try {
      const result = await generateSeasonalPlan(task, location, poolVolume);
      setPlan(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An error occurred.');
    } finally {
      setIsLoadingPlan(false);
    }
  };

  return (
    <div className="bg-slate-900/50 p-4 rounded-lg mt-6">
      <h3 className="text-xl font-bold text-white text-center">AI-Powered Seasonal Care</h3>
      <div className="mt-4">
        <label htmlFor="location" className="block text-sm font-medium text-slate-300">
          Your City, State or Zip Code:
        </label>
        <div className="flex gap-2 mt-1">
          <input
            type="text"
            id="location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="flex-grow bg-slate-700 border border-slate-600 rounded-md p-2 text-white"
            placeholder="e.g., Phoenix, AZ"
          />
          <button
            onClick={handleGetSuggestion}
            disabled={isLoadingSuggestion}
            className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-4 rounded-lg disabled:bg-slate-700"
          >
            {isLoadingSuggestion ? 'Thinking...' : 'Get Suggestion'}
          </button>
        </div>
      </div>
      {error && <p className="text-red-400 text-sm mt-2 text-center">{error}</p>}
      {suggestion && (
        <p className="text-cyan-300 bg-slate-800 p-3 rounded-md mt-3 text-center">{suggestion}</p>
      )}
      {waterBody === 'pool' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <button
            onClick={() => handleGeneratePlan('Winterizing')}
            disabled={isLoadingPlan}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-slate-700"
          >
            {isLoadingPlan ? 'Generating...' : 'Generate Winterizing Plan'}
          </button>
          <button
            onClick={() => handleGeneratePlan('Summer Start-up')}
            disabled={isLoadingPlan}
            className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-slate-700"
          >
            {isLoadingPlan ? 'Generating...' : 'Generate Summer Start-up Plan'}
          </button>
        </div>
      )}
      {waterBody === 'hot_tub' && (
        <div className="mt-4 bg-orange-900/30 border border-orange-700 rounded-lg p-3">
          <p className="text-orange-300 text-sm text-center">
            ♨️ Hot tubs are typically used year-round. Most are not winterized — just maintain water
            chemistry and keep the cover on. If closing seasonally, drain completely and follow the
            manufacturer's winterizing instructions.
          </p>
        </div>
      )}
      {plan && (
        <div className="mt-4 space-y-3">
          <h4 className="text-lg font-bold text-cyan-400">Your {plan.planType} Plan:</h4>
          {plan.steps
            .sort((a, b) => a.step - b.step)
            .map((step) => (
              <div key={step.step} className="bg-slate-800 p-3 rounded-lg border-l-4 border-slate-600">
                <h5 className="font-bold text-white">
                  {step.step}. {step.title}
                </h5>
                <p className="text-sm text-slate-300 mt-1">{step.description}</p>
                {step.chemicalsNeeded && step.chemicalsNeeded.length > 0 && (
                  <p className="text-xs text-slate-400 mt-2">
                    <b>Needed:</b> {step.chemicalsNeeded.join(', ')}
                  </p>
                )}
                {step.importantNote && (
                  <p className="text-xs text-yellow-400 mt-2 bg-yellow-900/50 p-2 rounded-md">
                    <b>Note:</b> {step.importantNote}
                  </p>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

export const MaintenanceTab: React.FC<MaintenanceTabProps> = ({ log, poolVolume, waterBody = 'pool' }) => {
  const isHotTub = waterBody === 'hot_tub';
  const routineTasks = isHotTub ? HOT_TUB_ROUTINE_TASKS : POOL_ROUTINE_TASKS;

  const [currentDate, setCurrentDate] = useState(new Date());
  const [location, setLocation] = useLocalStorage('poolLocation', '');
  const [maintenanceData, setMaintenanceData] = useState<{ [key: string]: string[] }>({});
  const [isCalendarLoading, setIsCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState('');

  useEffect(() => {
    const fetchCalendarData = async () => {
      if (!location) {
        setCalendarError('Please set a location below to generate a tailored calendar.');
        setMaintenanceData({});
        return;
      }
      setIsCalendarLoading(true);
      setCalendarError('');
      try {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const data = await generateMaintenanceCalendar(location, log, year, month);
        setMaintenanceData(data);
      } catch (e) {
        setCalendarError(e instanceof Error ? e.message : 'Failed to generate AI calendar.');
      } finally {
        setIsCalendarLoading(false);
      }
    };
    fetchCalendarData();
  }, [currentDate, log, location]);

  const startDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const changeMonth = (offset: number) =>
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));

  const handlePrint = (type: 'monthly' | 'weekly' | 'routine') => {
    const printWindow = window.open('', '', 'height=800,width=1000');
    if (!printWindow) return;
    const calendarHTML = document.getElementById('maintenance-calendar')?.innerHTML;
    const routineHTML = document.getElementById('routine-tasks')?.innerHTML;
    const tailwindCSS = `<script src="https://cdn.tailwindcss.com"></` + `script>`;
    const printStyles = document.querySelector('style')?.innerHTML || '';
    let content = '';
    if (type === 'monthly' && calendarHTML) content = `<div class="p-4">${calendarHTML}</div>`;
    else if (type === 'routine' && routineHTML) content = `<div class="p-4 print-open">${routineHTML}</div>`;
    else if (type === 'weekly') content = `<div class="p-4">${generateWeeklyHtml()}</div>`;

    printWindow.document.write(`
      <html>
        <head>
          <title>Print Maintenance Plan</title>
          ${tailwindCSS}
          <style>${printStyles}</style>
        </head>
        <body class="bg-white text-black">
          ${content}
          <script>setTimeout(() => { window.print(); window.close(); }, 500);</` + `script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const generateWeeklyHtml = () => {
    const today = new Date();
    const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
    let html = `<h2 class="text-xl font-bold mb-4">Weekly Maintenance Plan: ${startOfWeek.toLocaleDateString()}</h2><div class="space-y-3">`;
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(date.getDate() + i);
      const dayKey = date.toISOString().split('T')[0] ?? '';
      const tasks = maintenanceData[dayKey] ?? [];
      html += `<div class="border-b pb-2">
        <h3 class="font-bold">${date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</h3>
        <ul class="list-disc list-inside text-sm mt-1">
          ${tasks.map((t) => `<li>${t}</li>`).join('') || '<li>No specific tasks scheduled.</li>'}
        </ul>
      </div>`;
    }
    html += '</div>';
    return html;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Water body banner */}
      {isHotTub && (
        <div className="bg-orange-900/40 border border-orange-500 rounded-lg p-3 text-center">
          <p className="text-orange-300 font-semibold text-sm">
            ♨️ Hot Tub Maintenance Mode — Higher frequency tasks for spa water quality
          </p>
        </div>
      )}

      {/* AI Calendar */}
      <div id="maintenance-calendar" className="bg-slate-800 p-4 rounded-lg shadow-md">
        <div className="flex justify-between items-center mb-1">
          <button onClick={() => changeMonth(-1)} className="no-print bg-slate-700 p-2 rounded-full">
            &lt;
          </button>
          <h2 className="text-2xl font-bold text-white text-center">{monthName}</h2>
          <button onClick={() => changeMonth(1)} className="no-print bg-slate-700 p-2 rounded-full">
            &gt;
          </button>
        </div>
        <p className="text-center text-cyan-400 text-sm mb-4">AI-Powered Personalized Schedule</p>
        <div className="grid grid-cols-7 gap-1 text-center text-xs font-bold text-cyan-400">
          <div>Sun</div><div>Mon</div><div>Tue</div><div>Wed</div><div>Thu</div><div>Fri</div><div>Sat</div>
        </div>
        <div className="grid grid-cols-7 gap-1 mt-2 min-h-[250px] relative">
          {isCalendarLoading && (
            <div className="absolute inset-0 bg-slate-800/50 backdrop-blur-sm flex items-center justify-center z-10 rounded-lg">
              <p className="text-white text-lg">Generating tailored calendar...</p>
            </div>
          )}
          {calendarError && !isCalendarLoading && (
            <div className="absolute inset-0 bg-red-900/30 flex items-center justify-center z-10 rounded-lg col-span-7 p-4">
              <p className="text-red-300 text-center">{calendarError}</p>
            </div>
          )}
          {Array.from({ length: startDay }).map((_, i) => (
            <div key={`empty-${i}`} className="bg-slate-900/50 rounded-md" />
          ))}
          {Array.from({ length: daysInMonth }).map((_, day) => {
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day + 1);
            const dayKey = date.toISOString().split('T')[0] ?? '';
            const tasks = maintenanceData[dayKey] ?? [];
            return (
              <div key={day} className="bg-slate-900/50 p-2 rounded-md h-32 overflow-y-auto">
                <div className="font-bold text-white">{day + 1}</div>
                <ul className="text-xs text-slate-300 mt-1 list-disc list-inside">
                  {tasks.map((task, i) => (
                    <li key={i} className={task.includes('Trend') ? 'text-yellow-400' : ''}>
                      {task}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Print buttons */}
      <div className="bg-slate-800 p-4 rounded-lg shadow-md flex flex-wrap justify-center gap-3">
        <button
          onClick={() => handlePrint('monthly')}
          className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-2 px-4 rounded-lg"
        >
          Print Monthly Calendar
        </button>
        <button
          onClick={() => handlePrint('weekly')}
          className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-2 px-4 rounded-lg"
        >
          Print This Week's Plan
        </button>
        <button
          onClick={() => handlePrint('routine')}
          className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-2 px-4 rounded-lg"
        >
          Print Routine List
        </button>
      </div>

      {/* Routine checklist */}
      <div id="routine-tasks" className="bg-slate-800 p-4 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-white text-center mb-4">
          {isHotTub ? '♨️ Hot Tub Routine Checklist' : '🏊 Pool Routine Maintenance Checklist'}
        </h2>
        <div className="space-y-3">
          {Object.entries(routineTasks).map(([period, tasks]) => (
            <details key={period} className="bg-slate-900/50 p-3 rounded-lg group">
              <summary className="cursor-pointer font-semibold text-lg text-white list-none">
                {period}
              </summary>
              <div className="mt-2 pt-2 border-t border-slate-700 space-y-2">
                {tasks.map((task) => (
                  <div key={task.title}>
                    <h4 className="font-semibold text-slate-200">{task.title}</h4>
                    <p className="text-sm text-slate-400">{task.description}</p>
                  </div>
                ))}
              </div>
            </details>
          ))}
        </div>
      </div>

      <SeasonalCare
        poolVolume={poolVolume}
        location={location}
        setLocation={setLocation}
        waterBody={waterBody}
      />
    </div>
  );
};
